import { useState } from "react";
import { toast } from "sonner";
import { Field, Card, SaveButton, GhostButton, inputCls, areaCls } from "../ui";
import { adminFetch } from "../adminApi";

export default function ReviewsTab({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.reviews || [])));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (idx, key, value) =>
    setDraft((d) => d.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  const remove = (idx) => setDraft((d) => d.filter((_, i) => i !== idx));
  const add = () =>
    setDraft((d) => [...d, { id: `rev-${Date.now()}`, author: "Kunde", rating: 5, text: "", source: "", date: "", visible: true }]);
  const move = (idx, dir) =>
    setDraft((d) => {
      const copy = [...d];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return d;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/reviews", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Bewertungen gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Nur Bewertungen eintragen, die wirklich von echten Kunden stammen – nichts erfinden. Nur
        sichtbare Bewertungen erscheinen auf der Website. Die Reihenfolge hier entspricht der
        Website (Pfeile zum Sortieren).
      </p>

      {draft.map((r, idx) => (
        <Card
          key={r.id}
          title={r.author || `Bewertung ${idx + 1}`}
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`admin-review-visible-${r.id}`}
                onClick={() => update(idx, "visible", r.visible === false)}
                className={`h-9 rounded-full border px-4 text-xs font-semibold transition-colors ${
                  r.visible === false
                    ? "border-zinc-700 text-zinc-400"
                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                }`}
              >
                {r.visible === false ? "Versteckt" : "Sichtbar"}
              </button>
              <GhostButton onClick={() => move(idx, -1)} testid={`admin-review-up-${r.id}`}>
                ↑
              </GhostButton>
              <GhostButton onClick={() => move(idx, 1)} testid={`admin-review-down-${r.id}`}>
                ↓
              </GhostButton>
              <GhostButton danger onClick={() => remove(idx)} testid={`admin-review-delete-${r.id}`}>
                Löschen
              </GhostButton>
            </div>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name oder Kürzel">
              <input data-testid={`admin-review-author-${r.id}`} className={inputCls} value={r.author || ""} onChange={(e) => update(idx, "author", e.target.value)} maxLength={80} />
            </Field>
            <Field label="Quelle (z. B. Google)">
              <input className={inputCls} value={r.source || ""} onChange={(e) => update(idx, "source", e.target.value)} maxLength={60} />
            </Field>
            <Field label="Sterne (1–5)">
              <input
                type="number"
                min="1"
                max="5"
                className={inputCls}
                value={r.rating ?? 5}
                onChange={(e) => update(idx, "rating", Math.min(5, Math.max(1, Number(e.target.value) || 5)))}
              />
            </Field>
            <Field label="Datum (z. B. Oktober 2026)">
              <input className={inputCls} value={r.date || ""} onChange={(e) => update(idx, "date", e.target.value)} maxLength={20} />
            </Field>
          </div>
          <Field label="Bewertungstext">
            <textarea className={areaCls} value={r.text || ""} onChange={(e) => update(idx, "text", e.target.value)} maxLength={1500} />
          </Field>
        </Card>
      ))}

      <div className="sticky bottom-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-reviews" />
        <GhostButton onClick={add} testid="admin-review-add">+ Bewertung hinzufügen</GhostButton>
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.reviews || [])))} testid="admin-reset-reviews">
          Verwerfen
        </GhostButton>
      </div>
    </div>
  );
}
